import { useRef, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import { FileCode, FileText, Upload } from "lucide-react"
import { Button } from "../../../ui"
import { formatVND, PAYMENT_DOCUMENT_MAX_BYTES } from "../../../constants"
import { showToastError } from "utilities/toastUtil"

const XML_ACCEPT = ".xml,application/xml,text/xml"
const PDF_ACCEPT = "application/pdf,.pdf"

const FileBadge = ({ file, icon, onRemove, onView, viewLabel, disabled }) => (
  <div className="prc-file">
    <span className="prc-file-chip" title={file.name}>
      {icon}
      <span>{file.name}</span>
      <button type="button" aria-label={`Remove ${file.name}`} onClick={onRemove} disabled={disabled}>
        ×
      </button>
    </span>
    {onView && (
      <button type="button" className="prc-linkish" onClick={onView} disabled={disabled}>
        {viewLabel}
      </button>
    )}
  </div>
)

const UploadButton = ({ label, accept, disabled, onFile }) => {
  const ref = useRef(null)
  return (
    <>
      <button type="button" className="prc-upload" onClick={() => ref.current?.click()} disabled={disabled}>
        <Upload aria-hidden />
        {label}
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ""
          if (file) onFile(file)
        }}
      />
    </>
  )
}

const guardSize = (file) => {
  if (file.size > PAYMENT_DOCUMENT_MAX_BYTES) {
    showToastError(`${file.name} is larger than 10MB.`)
    return false
  }
  return true
}

const isPdf = (file) => {
  if (/\.pdf$/i.test(file.name) || file.type === "application/pdf") return true
  showToastError("Choose a PDF invoice.")
  return false
}

// The single invoice of a payment request: XML/PDF uploads plus the fields
// the XML fills. `children` adds extra fields into the same grid.
const InvoiceFields = ({ invoice, errors = {}, paymentType, disabled, onChange, onXml, children }) => {
  const [showParsed, setShowParsed] = useState(false)
  const pickXml = (file) => guardSize(file) && onXml(file)
  const pickPdf = (file) => guardSize(file) && isPdf(file) && onChange({ pdfFile: file })
  const data = invoice.xmlData

  return (
    <>
      <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {invoice.xmlFile ? (
          <FileBadge
            file={invoice.xmlFile}
            icon={<FileCode aria-hidden />}
            disabled={disabled}
            onRemove={() => onChange({ xmlFile: null, xmlStatus: null, xmlError: "", xmlData: null })}
            onView={data ? () => setShowParsed(true) : undefined}
            viewLabel="View data"
          />
        ) : (
          <UploadButton label="Upload XML" accept={XML_ACCEPT} disabled={disabled} onFile={pickXml} />
        )}
        {invoice.xmlFile && <UploadButton label="Replace XML" accept={XML_ACCEPT} disabled={disabled} onFile={pickXml} />}
        {invoice.pdfFile ? (
          <FileBadge
            file={invoice.pdfFile}
            icon={<FileText aria-hidden />}
            disabled={disabled}
            onRemove={() => onChange({ pdfFile: null })}
            onView={() => window.open(URL.createObjectURL(invoice.pdfFile), "_blank", "noopener,noreferrer")}
            viewLabel="View"
          />
        ) : (
          <UploadButton label="Upload invoice PDF" accept={PDF_ACCEPT} disabled={disabled} onFile={pickPdf} />
        )}
        {invoice.pdfFile && <UploadButton label="Replace PDF" accept={PDF_ACCEPT} disabled={disabled} onFile={pickPdf} />}
      </div>
      {invoice.xmlStatus === "ok" && <span className="prc-field-ok">Invoice parsed successfully</span>}
      {invoice.xmlStatus === "error" && <span className="prc-field-error">{invoice.xmlError}</span>}
      {(invoice.sellerName || invoice.invoiceAmount != null) && (
        <p className="prc-help">
          {[
            invoice.sellerName && `Invoice supplier: ${invoice.sellerName}`,
            invoice.invoiceAmount != null && `Invoice amount: ${formatVND(invoice.invoiceAmount)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      <div className="prc-grid-3">
        <div>
          <label className="prc-label">Invoice serial</label>
          <input className={`prc-input${errors.invoiceSerial ? " is-invalid" : ""}`} value={invoice.invoiceSerial} disabled={disabled} onChange={(e) => onChange({ invoiceSerial: e.target.value })} />
          {errors.invoiceSerial && <span className="prc-field-error">{errors.invoiceSerial}</span>}
        </div>
        <div>
          <label className="prc-label">Invoice number {paymentType !== "deposit" && <em>*</em>}</label>
          <input className={`prc-input${errors.invoiceNumber ? " is-invalid" : ""}`} value={invoice.invoiceNumber} disabled={disabled} onChange={(e) => onChange({ invoiceNumber: e.target.value })} />
          {errors.invoiceNumber && <span className="prc-field-error">{errors.invoiceNumber}</span>}
        </div>
        <div>
          <label className="prc-label">Invoice date</label>
          <input className="prc-input" type="date" value={invoice.invoiceDate} disabled={disabled} onChange={(e) => onChange({ invoiceDate: e.target.value })} />
        </div>
        <div>
          <label className="prc-label">Supplier tax code</label>
          <input className={`prc-input${errors.supplierTaxCode ? " is-invalid" : ""}`} value={invoice.supplierTaxCode} disabled={disabled} onChange={(e) => onChange({ supplierTaxCode: e.target.value })} />
          {errors.supplierTaxCode && <span className="prc-field-error">{errors.supplierTaxCode}</span>}
        </div>
        {children}
      </div>

      <Dialog
        open={showParsed && !!data}
        onClose={() => setShowParsed(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "pc-root", style: { minHeight: "auto", background: "var(--pc-surface)" } }}
      >
        <DialogTitle>Parsed invoice</DialogTitle>
        <DialogContent>
          {data && (
            <dl className="prc-parsed">
              <dt>Serial</dt>
              <dd>{data.invoiceSerial || "—"}</dd>
              <dt>Number</dt>
              <dd>{data.invoiceNumber || "—"}</dd>
              <dt>Date</dt>
              <dd>{data.invoiceDate || "—"}</dd>
              <dt>Amount</dt>
              <dd>{data.amount != null ? formatVND(data.amount) : "—"}</dd>
              <dt>Supplier</dt>
              <dd>{data.sellerName || "—"}</dd>
              <dt>Tax code</dt>
              <dd>{data.supplierTaxCode || "—"}</dd>
              <dt>Items</dt>
              <dd>{(data.items || []).join(", ") || "—"}</dd>
            </dl>
          )}
          <p className="prc-help">These values are copied into the invoice fields and stay editable.</p>
        </DialogContent>
        <DialogActions>
          <Button variant="primary" onClick={() => setShowParsed(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default InvoiceFields
